let cachedStatusBarHeight = null;
let cssProbeValue;
let waitTimer = null;

const STATUS_BAR_QUERY_KEYS = ['statusBarHeight', 'status_bar_height', 'safeAreaTop', 'safe_area_top'];

const supportsSafeAreaCSS = () => {
	if (typeof window === 'undefined' || typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
		return false;
	}
	return CSS.supports('padding-top: env(safe-area-inset-top)') || CSS.supports('padding-top: constant(safe-area-inset-top)');
};

const readFromQuery = () => {
	if (typeof window === 'undefined') return 0;
	try {
		const params = new URLSearchParams(window.location.search || '');
		for (const key of STATUS_BAR_QUERY_KEYS) {
			const raw = params.get(key);
			if (!raw) continue;
			const value = Number(raw);
			if (!Number.isNaN(value) && value > 0) {
				return value;
			}
		}
	} catch (error) {
		console.warn('[safeArea] Failed to parse query:', error);
	}
	return 0;
};

const readFromGlobalHints = () => {
	if (typeof window === 'undefined') return 0;
	const candidates = [
		window.__STATUS_BAR_HEIGHT__,
		window.__statusBarHeight__,
		window.__SAFE_AREA_TOP__,
		window.__safeAreaTop__
	];
	for (const candidate of candidates) {
		const value = Number(candidate);
		if (!Number.isNaN(value) && value > 0) {
			return value;
		}
	}
	return 0;
};

const probeCssSafeArea = () => {
	if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
	if (cssProbeValue !== undefined) return cssProbeValue;
	if (!supportsSafeAreaCSS()) {
		cssProbeValue = 0;
		return cssProbeValue;
	}
	const container = document.body || document.documentElement;
	if (!container) {
		cssProbeValue = 0;
		return cssProbeValue;
	}
	const el = document.createElement('div');
	el.style.cssText = [
		'position:absolute',
		'top:0',
		'left:0',
		'width:0',
		'height:0',
		'visibility:hidden',
		'pointer-events:none',
		'padding-top:constant(safe-area-inset-top)',
		'padding-top:env(safe-area-inset-top)'
	].join(';');
	container.appendChild(el);
	const computed = window.getComputedStyle(el);
	const value = parseFloat(computed.paddingTop) || 0;
	container.removeChild(el);
	cssProbeValue = Math.round(value);
	return cssProbeValue;
};

const fallbackByUA = () => {
	if (typeof navigator === 'undefined') return 0;
	const ua = navigator.userAgent || '';
	if (/iphone|ipad|ipod/i.test(ua)) {
		return 44;
	}
	return 24;
};

const setGlobalSafeArea = (height) => {
	if (typeof window === 'undefined') return;
	window.__H5_SAFE_AREA_TOP__ = height;
};

const syncUniConfig = (height) => {
	if (typeof window === 'undefined') return;
	const config = window.__uniConfig;
	if (!config || typeof config !== 'object') return;
	const ensureSafeArea = (target = {}) => {
		if (!target || typeof target !== 'object') return;
		if (typeof target.statusBarHeight !== 'number' || target.statusBarHeight <= 0) {
			target.statusBarHeight = height;
		}
		if (typeof target.windowTop !== 'number' || target.windowTop <= 0) {
			target.windowTop = height;
		}
		if (target.safeArea && typeof target.safeArea === 'object') {
			target.safeArea.top = height;
		}
	};
	ensureSafeArea(config);
	if (config.window && typeof config.window === 'object') {
		ensureSafeArea(config.window);
	}
	if (Array.isArray(config.pages)) {
		config.pages.forEach((page) => {
			if (page && page.style && page.style.navigationStyle !== 'custom') {
				if (typeof page.style.windowTop !== 'number' || page.style.windowTop <= 0) {
					page.style.windowTop = height;
				}
			}
		});
	}
};

const resolveStatusBarHeight = () => {
	if (cachedStatusBarHeight !== null) return cachedStatusBarHeight;
	const byQuery = readFromQuery();
	if (byQuery > 0) {
		cachedStatusBarHeight = byQuery;
		return cachedStatusBarHeight;
	}
	const byGlobal = readFromGlobalHints();
	if (byGlobal > 0) {
		cachedStatusBarHeight = byGlobal;
		return cachedStatusBarHeight;
	}
	const byCSS = probeCssSafeArea();
	if (byCSS > 0) {
		cachedStatusBarHeight = byCSS;
		return cachedStatusBarHeight;
	}
	cachedStatusBarHeight = fallbackByUA();
	return cachedStatusBarHeight;
};

const applyCssVariables = (height) => {
	if (typeof document === 'undefined' || !document.documentElement) return;
	const root = document.documentElement;
	if (height > 0) {
		root.style.setProperty('--status-bar-height', `${height}px`);
		root.style.setProperty('--safe-area-inset-top', `${height}px`);
		root.style.setProperty('--h5-safe-area-inset-top', `${height}px`);
		root.style.setProperty('--window-top', `${height}px`);
	} else {
		root.style.removeProperty('--status-bar-height');
		root.style.removeProperty('--safe-area-inset-top');
		root.style.removeProperty('--h5-safe-area-inset-top');
		root.style.removeProperty('--window-top');
	}
};

const withStatusBarHeight = (info, height) => {
	if (!info || typeof info !== 'object') return info;
	if (!height || height <= 0) return info;
	if (!info.statusBarHeight || info.statusBarHeight <= 0) {
		info.statusBarHeight = height;
	}
	if (info.safeArea && info.safeArea.top === 0) {
		info.safeArea.top = height;
	}
	if (info.windowTop === 0) {
		info.windowTop = height;
	}
	return info;
};

const patchUniWhenReady = (height) => {
	if (typeof uni === 'undefined') {
		if (waitTimer) return;
		let tries = 0;
		waitTimer = setInterval(() => {
			if (typeof uni !== 'undefined' || tries > 300) {
				clearInterval(waitTimer);
				waitTimer = null;
				if (typeof uni !== 'undefined') {
					patchUniWhenReady(height);
				}
			}
			tries += 1;
		}, 30);
		return;
	}
	const tryPatch = (methodName) => {
		const original = uni[methodName];
		if (typeof original !== 'function') return;
		if (original.__withSafeAreaPatch) return;
		const patched = (...args) => {
			const info = original.apply(uni, args);
			return withStatusBarHeight(info, getStatusBarHeight());
		};
		patched.__withSafeAreaPatch = true;
		uni[methodName] = patched;
	};
	tryPatch('getSystemInfoSync');
	tryPatch('getWindowInfo');
};

const applyHeight = (height) => {
	if (!height || Number.isNaN(height)) return;
	cachedStatusBarHeight = Math.max(0, Math.round(Number(height)));
	applyCssVariables(cachedStatusBarHeight);
	setGlobalSafeArea(cachedStatusBarHeight);
	syncUniConfig(cachedStatusBarHeight);
	patchUniWhenReady(cachedStatusBarHeight);
};

const listenNativeMessages = () => {
	if (typeof window === 'undefined' || window.__safeAreaListenerAttached) return;
	window.__safeAreaListenerAttached = true;
	const handler = (event) => {
		const data = event?.data;
		if (!data || typeof data !== 'object') return;
		const value = Number(
			data.statusBarHeight ??
			data.safeAreaTop ??
			(data.safeArea && data.safeArea.top)
		);
		if (!Number.isFinite(value) || value <= 0) return;
		applyHeight(value);
	};
	window.addEventListener('message', handler);
	window.__setNativeSafeArea = (value) => {
		applyHeight(value);
	};
};

export const ensureSafeAreaPolyfill = () => {
	const height = resolveStatusBarHeight();
	applyHeight(height);
	listenNativeMessages();
};

export const getStatusBarHeight = () => resolveStatusBarHeight();
